/**
 * Punctuation and whitespace sanitizer across all 4 supported languages:
 * English, Hindi, Gujarati, Marathi.
 *
 * Prevents spacing issues before punctuation (such as "papaya .")
 * and normalizes sentence-ending punctuation.
 */

export const sanitizeInstruction = (str, lang = 'english') => {
  if (!str || typeof str !== 'string') return '';

  // 1. Strip soft hyphens, zero-width space, BOM, format marks (preserve ZWJ/ZWNJ for Indic ligatures)
  let text = str
    .replace(/[\u00AD\u200B\u2060\uFEFF\u200E\u200F]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();

  if (!text) return '';

  // Return empty string if text has no letters or digits (e.g. only punctuation like '.', '।') or is 'not provided'
  const content = text.replace(/[.,;:!?।॥\s\-_/\\()[\]{}'"`~@#$%^&*+=<>|]/g, '');
  if (!content || text.toLowerCase() === 'not provided') return '';

  // Strip accidental leading punctuation marks or bullet characters (e.g. ".Avoid" -> "Avoid")
  text = text.replace(/^[.,;:!?।॥\s\-_•*]+/, '');

  // 2. Collapse multiple spaces and tabs into a single space
  text = text.replace(/[ \t]+/g, ' ');

  // 3. Remove whitespace inside parentheses: "( word )" -> "(word)"
  text = text.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');

  // 4. Remove any whitespace before punctuation marks in English & Indic scripts
  // Supported punctuation: . , ; : ! ? । (Devanagari danda) ॥ (double danda)
  text = text.replace(/\s+([.,;:!?।॥])/g, '$1');

  // Ensure single space after punctuation if immediately followed by another word
  text = text.replace(/([,;:!?।॥])(?=[^\s,;:!?।॥\d)\]}])/g, '$1 ');

  // 5. Clean up accidental duplicate punctuation
  text = text.replace(/\.{2,}/g, '.');
  text = text.replace(/।{2,}/g, '।');
  text = text.replace(/[,;:]\s*([.।])/g, '$1');

  // 6. Ensure appropriate sentence-ending punctuation for instructions:
  // - Hindi: normalize trailing period to danda । or append danda ।
  // - Marathi, Gujarati & English: ensure period . if missing
  if (lang === 'hindi') {
    if (text.endsWith('.')) {
      text = text.slice(0, -1) + '।';
    } else if (!/[!?।॥]$/.test(text)) {
      text += '।';
    }
  } else {
    if (!/[.!?।॥]$/.test(text)) {
      text += '.';
    }
  }

  return text;
};

export const sanitizeFoodOrName = (str) => {
  if (!str || typeof str !== 'string') return '';
  let text = str
    .replace(/[\u00AD\u200B\u2060\uFEFF\u200E\u200F]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\(\s+/g, '(').replace(/\s+\)/g, ')')
    .replace(/\s+([.,;:!?।॥])/g, '$1')
    .replace(/([,;:!?।॥])(?=[^\s,;:!?।॥\d)\]}])/g, '$1 ')
    .trim();

  const content = text.replace(/[.,;:!?।॥\s\-_/\\()[\]{}'"`~@#$%^&*+=<>|]/g, '');
  if (!content || text.toLowerCase() === 'not provided') return '';

  return text;
};
